import { useState } from "react";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { PAIN_CATEGORIES, TREATMENT_GROUPS } from "@/data/site";
import { Reveal } from "@/components/Reveal";

export const Pricing = () => {
  const lenis = useLenis();
  const [open, setOpen] = useState(0);

  const book = () => {
    const el = document.getElementById("contact");
    if (el && lenis) lenis.scrollTo(el, { duration: 1.4 });
  };

  const toggle = (i) => setOpen((prev) => (prev === i ? -1 : i));

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-cream px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1000px]">
        <Reveal>
          <div className="mb-16 text-center md:mb-20">
            <p className="mb-6 text-[0.7rem] uppercase tracking-[0.3em] text-sage">
              True Wealth is Good Health
            </p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
              Treatments &amp; <span className="italic text-forest">packages</span>
            </h2>
          </div>
        </Reveal>

        <Reveal>
          <p className="mx-auto mb-10 max-w-2xl text-center text-base font-light leading-relaxed text-stone">
            We categorise pain, disease and illness into three categories to help us
            provide the best treatment protocol for you:
          </p>
          <div className="mb-20 grid gap-px overflow-hidden rounded-2xl border border-sage/30 bg-sage/30 sm:grid-cols-3">
            {PAIN_CATEGORIES.map((c) => (
              <div
                key={c.name}
                data-testid={`pain-category-${c.name.toLowerCase()}`}
                className="bg-cream p-8"
              >
                <h3 className="font-serif text-2xl text-forest md:text-3xl">{c.name}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-stone">
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        <p className="mb-8 text-center text-[0.7rem] uppercase tracking-[0.3em] text-sage">
          Our Treatments
        </p>
        <div className="border-t border-sage/40">
          {TREATMENT_GROUPS.map((g, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={g.name} delay={i * 0.06}>
                <div
                  data-testid={`treatment-group-${i}`}
                  className={`border-b ${
                    g.featured ? "border-forest/40" : "border-sage/40"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`treatment-toggle-${i}`}
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-7 text-left md:gap-10 md:py-9"
                  >
                    <div className="flex-1">
                      <h3
                        className={`font-serif text-2xl tracking-tight md:text-3xl ${
                          g.featured ? "italic text-forest" : "text-ink"
                        }`}
                      >
                        {g.name}
                      </h3>
                      <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-stone">
                        {g.summary}
                      </p>
                    </div>
                    <motion.span
                      animate={{ rotate: isOpen ? 135 : 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-1 shrink-0 text-sage"
                    >
                      <Plus size={26} strokeWidth={1.5} />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-8 pt-1">
                          {g.items.map((item, j) => (
                            <div
                              key={item.name}
                              data-testid={`treatment-item-${i}-${j}`}
                              className="flex items-start justify-between gap-6 border-t border-sage/25 py-5 md:gap-10"
                            >
                              <div className="flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-3">
                                  <h4 className="font-serif text-lg tracking-tight text-forest md:text-xl">
                                    {item.name}
                                  </h4>
                                  {item.duration && (
                                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-sage">
                                      {item.duration}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-stone">
                                  {item.desc}
                                </p>
                                {item.followUp && (
                                  <p className="mt-2 text-[0.65rem] uppercase tracking-[0.2em] text-sage">
                                    Follow-up {item.followUp}
                                  </p>
                                )}
                              </div>
                              <span className="whitespace-nowrap font-serif text-2xl text-forest md:text-3xl">
                                {item.price}
                              </span>
                            </div>
                          ))}
                          {g.note && (
                            <p
                              data-testid={`treatment-note-${i}`}
                              className="mt-4 border-t border-sage/25 pt-4 text-xs font-light italic leading-relaxed text-sage"
                            >
                              {g.note}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col items-center gap-5 text-center">
            <p className="max-w-md text-sm font-light leading-relaxed text-stone">
              Not sure which treatment is right for you? Reach out and we'll craft a
              plan around your needs.
            </p>
            <button
              data-testid="pricing-book-btn"
              onClick={book}
              className="rounded-full bg-forest px-9 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-moss"
            >
              Enquire &amp; Book
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
